import { useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useCachedGet } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { fechaSolo, fechaHora } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PackageOpen, Trello, ChevronRight, Trash2 } from "lucide-react";

// Las canceladas y entregadas salen de la vista por default: solo hacen ruido junto a las que siguen en curso.
const GRUPOS = [
  { id: "en_curso", label: "En curso" },
  { id: "entregadas", label: "Entregadas" },
  { id: "canceladas", label: "Canceladas" },
];
const grupoDe = (m) => {
  if (m.cancelada) return "canceladas";
  if (["pendiente_confirmacion", "confirmada"].includes(m.confirmacion_entrega_estado)) return "entregadas";
  return "en_curso";
};

export default function Muestras() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [verEliminadas, setVerEliminadas] = useState(false);
  const [grupo, setGrupo] = useState("en_curso");
  const { data: items } = useCachedGet("/muestras");
  const { data: eliminadas } = useCachedGet(user?.es_admin ? "/muestras-eliminadas" : null);

  const puedeVer = user?.es_admin || user?.perms?.acciones?.ver_muestras || user?.perms?.acciones?.gestionar_muestras;
  if (user && !puedeVer) return <Navigate to="/" replace />;

  const cuenta = (g) => (items || []).filter((m) => grupoDe(m) === g).length;
  const lista = verEliminadas ? (eliminadas || []) : items ? items.filter((m) => grupoDe(m) === grupo) : null;
  const vacio = verEliminadas ? "No hay muestras eliminadas."
    : (items || []).length === 0 ? "Aún no hay pedidos de muestra autorizados."
    : `No hay muestras ${GRUPOS.find((g) => g.id === grupo).label.toLowerCase()}.`;

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><PackageOpen size={18} /></div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Muestras</h1>
          </div>
          <p className="text-slate-500 mt-1">Pedidos de muestra autorizados. Da clic para ver el detalle.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.es_admin && (
            <button
              onClick={() => setVerEliminadas((v) => !v)}
              data-testid="toggle-eliminadas"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${verEliminadas ? "bg-red-600 text-white border-red-600 hover:bg-red-700" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            >
              <Trash2 size={16} /> {verEliminadas ? "Ver activas" : "Ver eliminadas"}
            </button>
          )}
          {!verEliminadas && (
            <Link to="/muestras/tablero" data-testid="ir-tablero" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors">
              <Trello size={16} /> Tablero de productos
            </Link>
          )}
        </div>
      </div>

      {!verEliminadas && (
        <div className="flex gap-1 mb-4 p-1 bg-slate-100 rounded-lg w-fit" data-testid="filtro-muestras">
          {GRUPOS.map((g) => (
            <button
              key={g.id}
              onClick={() => setGrupo(g.id)}
              data-testid={`filtro-${g.id}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${grupo === g.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              {g.label} <span className="text-xs text-slate-400 tabular-nums">{items ? cuenta(g.id) : ""}</span>
            </button>
          ))}
        </div>
      )}

      {!lista ? (
        <Skeleton className="h-72 rounded-xl" />
      ) : lista.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">{vacio}</Card>
      ) : (
        <Card className="border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
          {lista.map((m) => (
            <button
              key={m.id}
              onClick={() => navigate(`/muestras/${m.id}`)}
              className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
              data-testid={`muestra-item-${m.id}`}
            >
              <span className="font-mono text-sm font-bold text-slate-800 w-24 shrink-0">{m.folio}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{m.cliente_nombre}</div>
                <div className="text-xs text-slate-400">
                  {m.productos.length} producto(s) · Requerida: {m.fecha_requerida ? fechaSolo(m.fecha_requerida) : "—"}
                  {verEliminadas && m.deleted_by_nombre && ` · Eliminada por ${m.deleted_by_nombre}${m.deleted_at ? " el " + fechaSolo(m.deleted_at) : ""}`}
                </div>
              </div>
              {verEliminadas ? (
                <span className="text-[11px] font-semibold text-red-600 shrink-0">Eliminada</span>
              ) : m.cancelada ? (
                <span
                  className="w-32 shrink-0 text-right cursor-help"
                  title={`Cancelada por ${m.cancelada_por_nombre || "—"}. Motivo: ${m.motivo_cancelacion_solicitud || "—"}`}
                  data-testid={`muestra-cancelada-${m.id}`}
                >
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700">Cancelada</span>
                  {m.cancelada_en && <span className="block text-[11px] text-slate-400 mt-1">{fechaHora(m.cancelada_en)}</span>}
                </span>
              ) : (
                <div className="w-32 shrink-0 hidden sm:block">
                  <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1"><span>Avance</span><span>{m.progreso}%</span></div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${m.progreso}%` }} /></div>
                </div>
              )}
              <ChevronRight size={18} className="text-slate-300 shrink-0" />
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}
